import { create } from 'zustand';
import type { AdminCourse } from '@/domain/model/AdminCourse';
import type { CreateCourseRequest } from '@/application/dto/CreateCourseRequest';
import type { UpdateCourseRequest } from '@/application/dto/UpdateCourseRequest';
import type { CourseUploadFiles, NewCourseFiles } from '@/domain/model/CourseUploadFiles';
import { adminService } from '@/infrastructure/store/adminServiceInstance';
import { resolveErrorKey } from '@/infrastructure/util/error';

interface AdminCourseState {
  courses: AdminCourse[];
  isLoading: boolean;
  uploadProgress: number | null;
  error: string | null;
}

interface AdminCourseActions {
  fetchCourses: (chapterId?: string) => Promise<void>;
  createCourse: (data: CreateCourseRequest, files: NewCourseFiles) => Promise<boolean>;
  updateCourse: (
    id: string,
    data: UpdateCourseRequest,
    files: CourseUploadFiles,
  ) => Promise<boolean>;
  deleteCourse: (id: string) => Promise<boolean>;
  removeCourseDocument: (courseId: string, documentId: string) => Promise<boolean>;
  clearError: () => void;
}

const hasFilesToUpload = (files: CourseUploadFiles): boolean =>
  files.media !== undefined || files.documents.length > 0;

export const useAdminCourseStore = create<AdminCourseState & AdminCourseActions>((set) => ({
  courses: [],
  isLoading: false,
  uploadProgress: null,
  error: null,

  fetchCourses: async (chapterId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const courses = await adminService.getCourses(chapterId);
      set({ courses, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.courseLoadFailed') });
    }
  },

  createCourse: async (data: CreateCourseRequest, files: NewCourseFiles) => {
    set({ isLoading: true, uploadProgress: 0, error: null });
    try {
      await adminService.createCourse(data, files, (progress) => {
        set({ uploadProgress: progress });
      });
      set({ isLoading: false, uploadProgress: null });
      return true;
    } catch (e) {
      set({
        isLoading: false,
        uploadProgress: null,
        error: resolveErrorKey(e, 'admin.courseSaveFailed'),
      });
      return false;
    }
  },

  updateCourse: async (id: string, data: UpdateCourseRequest, files: CourseUploadFiles) => {
    const isUploading = hasFilesToUpload(files);
    set({ isLoading: true, uploadProgress: isUploading ? 0 : null, error: null });
    try {
      await adminService.updateCourse(
        id,
        data,
        files,
        isUploading
          ? (progress) => {
              set({ uploadProgress: progress });
            }
          : undefined,
      );
      set({ isLoading: false, uploadProgress: null });
      return true;
    } catch (e) {
      set({
        isLoading: false,
        uploadProgress: null,
        error: resolveErrorKey(e, 'admin.courseSaveFailed'),
      });
      return false;
    }
  },

  deleteCourse: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await adminService.deleteCourse(id);
      set({ isLoading: false });
      return true;
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.courseDeleteFailed') });
      return false;
    }
  },

  removeCourseDocument: async (courseId: string, documentId: string) => {
    set({ isLoading: true, error: null });
    try {
      await adminService.removeCourseDocument(courseId, documentId);
      set({ isLoading: false });
      return true;
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.courseDeleteFailed') });
      return false;
    }
  },

  clearError: () => set({ error: null }),
}));
